/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: GatewaySipProfileFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Gateway SIP Profiles module.
----------------------------------------------------------------*/

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Cable, Phone, Music, Settings as SettingsIcon, Lock, Bug, Search, Copy, Plus } from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { useNotifications } from '../../contexts/NotificationContext';
import { Toggle } from '../../components/ui/Toggle';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { CodecField } from '../../components/ui/CodecField';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { useAuthStore } from '../../store/auth';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import {
  navigateToGatewaySipProfileDuplicate,
} from './gatewaySipProfilesActions';
import {
  fetchGatewaySipProfile,
  createGatewaySipProfile,
  updateGatewaySipProfile,
  deleteGatewaySipProfile,
  fetchGatewaySipProfiles,
} from './api';
import { GatewaySipProfilePayload, GatewaySipProfile } from './types/gatewaySipProfile.types';

interface SipProfile {
  id: number;
  name: string;
}

interface GatewaySipProfileFormData {
  name: string;
  description: string;
  enabled: boolean;
  baseSipProfileId: number | null;
  isDefault: boolean;

  // All settings as key-value pairs
  settings: Record<string, string | boolean | number>;
}

// Category configuration with icons
const CATEGORIES = [
  { id: 1, key: 'signaling', icon: Phone, labelKey: 'sections.signaling' },
  { id: 2, key: 'media', icon: Music, labelKey: 'sections.media' },
  { id: 3, key: 'behavior', icon: SettingsIcon, labelKey: 'sections.behavior' },
  { id: 4, key: 'security', icon: Lock, labelKey: 'sections.security' },
  { id: 5, key: 'logging', icon: Bug, labelKey: 'sections.logging' },
];

// Field configuration by category
type FieldType = 'codec' | 'toggle' | 'select' | 'number' | 'text' | 'password';

interface FieldDefinition {
  name: string;
  fsName?: string;
  type: FieldType;
  default: string | number | boolean;
  options?: string[];
  possibleValues?: string;
  min?: number;
  max?: number;
  optional?: boolean;
  dependsOn?: string;
  placeholder?: string;
  hidden?: boolean;
  videoOnly?: boolean;
}

const FIELD_CONFIG: Record<string, FieldDefinition[]> = {
  signaling: [
    { name: 'transport', fsName: 'transport', type: 'select', default: 'udp', options: ['udp', 'tcp', 'tls'], possibleValues: 'udp | tcp | tls' },
    { name: 'dtmfMode', fsName: 'dtmf_mode', type: 'select', default: 'rfc2833', options: ['rfc2833', 'info', 'inband', 'auto'], possibleValues: 'rfc2833 | info | inband | auto' },
    { name: 'inviteFormat', fsName: 'invite_format', type: 'select', default: 'username@domain', options: ['username@domain', 'E.164', 'user-only'], possibleValues: 'username@domain | E.164 | user-only' },
    { name: 'calleridPolicy', fsName: 'callerid_policy', type: 'select', default: 'use-from', options: ['use-from', 'use-pai', 'use-rpid'], possibleValues: 'use-from | use-pai | use-rpid' },
    { name: 'natMapping', fsName: 'nat_mapping', type: 'toggle', default: true, possibleValues: 'true | false' },
    { name: 'forceContactRewrite', fsName: 'force_contact_rewrite', type: 'toggle', default: true, possibleValues: 'true | false' },
    { name: 'registerTransport', fsName: 'register_transport', type: 'select', default: 'udp', options: ['udp', 'tcp', 'tls'], possibleValues: 'udp | tcp | tls' },
    { name: 'sipTimerT1', fsName: 'sip_timer_t1', type: 'number', default: 500, min: 100, max: 2000, possibleValues: 'Any positive integer' },
    { name: 'sipTimerT1x64', fsName: 'sip_timer_t1x64', type: 'number', default: 32000, min: 10000, max: 64000, possibleValues: 'Any positive integer' },
  ],
  media: [
    { name: 'codecPrefs', fsName: 'codec_prefs', type: 'codec', default: 'PCMU,PCMA,G722', possibleValues: 'PCMU | PCMA | G729 | G722 | OPUS | CELT | AMR' },
    { name: 'allowVideo', fsName: 'allow_video', type: 'toggle', default: false, possibleValues: 'true | false', hidden: true },
    { name: 'videoCodecPrefs', fsName: 'video_codec_prefs', type: 'codec', default: '', possibleValues: 'VP8 | H264 | H263 | AV1', videoOnly: true },
    { name: 'rtpProxy', fsName: 'rtp_proxy', type: 'toggle', default: true, possibleValues: 'true | false' },
    { name: 'forceRtpProxy', fsName: 'force_rtp_proxy', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'rtpIp', fsName: 'rtp_ip', type: 'text', default: 'auto', possibleValues: 'auto | IP address' },
    { name: 'extRtpIp', fsName: 'ext_rtp_ip', type: 'text', default: 'auto-nat', possibleValues: 'IP address | auto-nat' },
    { name: 'rtcpInterval', fsName: 'rtcp_interval', type: 'number', default: 5, min: 5, max: 60, possibleValues: 'Integer (e.g. 560)' },
    { name: 'ptime', fsName: 'ptime', type: 'number', default: 20, min: 10, max: 40, possibleValues: '10 | 20 | 30 | 40' },
    { name: 't38Support', fsName: 't38_support', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'mediaTimeout', fsName: 'media_timeout', type: 'number', default: 60, min: 10, max: 600, possibleValues: 'Any integer' },
  ],
  behavior: [
    { name: 'sessionTimer', fsName: 'session_timer', type: 'number', default: 1800, min: 300, max: 7200, possibleValues: 'Seconds (e.g. 1800)' },
    { name: 'sessionRefresher', fsName: 'session_refresher', type: 'select', default: 'uas', options: ['uac', 'uas', 'auto'], possibleValues: 'uac | uas | auto' },
    { name: 'inboundCodecNegotiation', fsName: 'inbound_codec_negotiation', type: 'select', default: 'greedy', options: ['greedy', 'generous'], possibleValues: 'greedy | generous' },
    { name: 'mediaTimeoutMode', fsName: 'media_timeout_mode', type: 'select', default: 'drop', options: ['drop', 'ignore'], possibleValues: 'drop | ignore' },
    { name: 'maxSessions', fsName: 'max_sessions', type: 'number', default: 100, min: 1, max: 1000, possibleValues: 'Integer' },
    { name: 'holdMusic', fsName: 'hold_music', type: 'text', default: 'local_stream://moh', possibleValues: 'local_stream://moh | file path' },
  ],
  security: [
    { name: 'tlsEnabled', fsName: 'tls_enabled', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'tlsVerifyPolicy', fsName: 'tls_verify_policy', type: 'select', default: 'none', options: ['none', 'peer', 'self-signed', 'optional'], dependsOn: 'tlsEnabled', possibleValues: 'none | peer | self-signed | optional' },
    { name: 'tlsVersion', fsName: 'tls_version', type: 'select', default: '1.2', options: ['1.0', '1.1', '1.2', '1.3'], dependsOn: 'tlsEnabled', possibleValues: '1.0 | 1.1 | 1.2 | 1.3' },
    { name: 'tlsCertDir', fsName: 'tls_cert_dir', type: 'text', default: '/etc/freeswitch/tls', dependsOn: 'tlsEnabled', possibleValues: 'Filesystem path' },
    { name: 'tlsPassphrase', fsName: 'tls_passphrase', type: 'text', default: '', dependsOn: 'tlsEnabled', possibleValues: 'String' },
    { name: 'rtpSecureMedia', fsName: 'rtp-secure-media', type: 'select', default: 'optional', options: ['true', 'optional', 'false'], possibleValues: 'true | optional | false' },
    { name: 'requireSecureMedia', fsName: 'require-secure-media', type: 'select', default: 'false', options: ['true', 'false'], possibleValues: 'true | false' },
  ],
  logging: [
    { name: 'sofiaDebugLevel', fsName: 'sofia_debug_level', type: 'number', default: 0, min: 0, max: 9, possibleValues: '09' },
    { name: 'logAuthFailures', fsName: 'log_auth_failures', type: 'toggle', default: false, possibleValues: 'true | false' },
    { name: 'sipTrace', fsName: 'sip_trace', type: 'toggle', default: false, possibleValues: 'true | false' },
  ],
};

import { GatewaySipProfileSettingsTabs } from './components/GatewaySipProfileSettingsTabs';

export default function GatewaySipProfileFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = id && id !== 'new';
  const navigate = useNavigate();
  const { t } = useTranslation(['gatewaySipProfiles', 'common']);
  const { showSuccess, showError, showInfo } = useNotifications();
  const { user } = useAuthStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sipProfiles, setSipProfiles] = useState<SipProfile[]>([]);
  const [activeTab, setActiveTab] = useState('signaling'); // Default tab
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Record<string, number>>({}); // Count of results per tab

  // Quick List states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProfiles, setQuickListProfiles] = useState<GatewaySipProfile[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  const [formData, setFormData] = useState<GatewaySipProfileFormData>({
    name: '',
    description: '',
    enabled: true,
    baseSipProfileId: null,
    isDefault: false,
    settings: {},
  });

  const tenantDomainId = useMemo(() => {
    if (!user?.tenant?.id) {
      return undefined;
    }
    const parsed = Number(user.tenant.id);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [user?.tenant?.id]);

  const [originalData, setOriginalData] = useState<GatewaySipProfileFormData>({
    name: '',
    description: '',
    enabled: true,
    baseSipProfileId: null,
    isDefault: false,
    settings: {},
  });

  const cloneFormData = useCallback((data: GatewaySipProfileFormData): GatewaySipProfileFormData => ({
    ...data,
    settings: { ...data.settings },
  }), []);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = searchParams.get('_reset');

    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      !duplicateId &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      const defaultSettings: Record<string, string | boolean | number> = {};
      Object.values(FIELD_CONFIG).flat().forEach((field) => {
        defaultSettings[field.name] = field.default;
      });

      setFormData({
        name: '',
        description: '',
        enabled: true,
        baseSipProfileId: null,
        isDefault: false,
        settings: defaultSettings,
      });
      setOriginalData({
        name: '',
        description: '',
        enabled: true,
        baseSipProfileId: null,
        isDefault: false,
        settings: defaultSettings,
      });

      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, duplicateId, searchParams]);

  // Initialize default settings
  useEffect(() => {
    const defaultSettings: Record<string, string | boolean | number> = {};
    Object.values(FIELD_CONFIG).flat().forEach((field) => {
      defaultSettings[field.name] = field.default;
    });
    setFormData((prev) => ({
      ...prev,
      settings: { ...defaultSettings, ...prev.settings },
    }));
  }, []);

  // Load SIP Profiles for dropdown
  useEffect(() => {
    const controller = new AbortController();

    const fetchSipProfiles = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/sip-profiles?limit=100'), {
          headers: getApiHeaders(),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('FAILED_TO_LOAD');
        }

        const result = await response.json();
        setSipProfiles(result.data || []);
      } catch (error) {
        if (!controller.signal.aborted) {
          showError(t('gatewaySipProfiles:loadError'));
        }
      }
    };

    fetchSipProfiles();

    return () => controller.abort();
  }, [showError, t]);

  // Load profile data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchProfile = async () => {
        setIsLoading(true);
        try {
          const result = await fetchGatewaySipProfile(id);
          const profile = result.data;

          // Map settings to form data
          const settingsMap: Record<string, string | boolean | number> = {};
          profile.settings?.forEach((setting) => {
            const field = Object.values(FIELD_CONFIG)
              .flat()
              .find((f) => f.name === setting.name);

            if (field) {
              if (field.type === 'toggle') {
                settingsMap[setting.name] = setting.value === 'true';
              } else if (field.type === 'number') {
                settingsMap[setting.name] = setting.value ? parseInt(setting.value) : field.default;
              } else {
                settingsMap[setting.name] = setting.value || field.default;
              }
            }
          });

          const loadedData = {
            name: profile.name || '',
            description: profile.description || '',
            enabled: profile.enabled ?? true,
            baseSipProfileId: profile.baseSipProfileId || null,
            isDefault: profile.isDefault || false,
            settings: settingsMap,
          };

          const clonedData = cloneFormData(loadedData);
          setFormData(clonedData);
          setOriginalData(clonedData); // Set original data for change detection
        } catch (error) {
          const msg = error instanceof Error ? error.message : t('gatewaySipProfiles:loadError');
          showError(msg);
        } finally {
          setIsLoading(false);
        }
      };

      fetchProfile();
    }
  }, [cloneFormData, id, isEditMode, showError, t]);


  // Smart search: Calculate results per tab
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults({});
      return;
    }

    const query = searchQuery.toLowerCase();
    const results: Record<string, number> = {};

    // Count matches per category
    CATEGORIES.forEach((category) => {
      const categoryFields = FIELD_CONFIG[category.key] || [];
      const matchCount = categoryFields.filter((field) => {
        if (field.hidden) return false;

        const fieldLabel = t(`gatewaySipProfiles:${field.name}`).toLowerCase();
        const fieldFsName = (field.fsName || '').toLowerCase();
        const fieldName = field.name.toLowerCase();

        return (
          fieldLabel.includes(query) ||
          fieldFsName.includes(query) ||
          fieldName.includes(query)
        );
      }).length;

      results[category.key] = matchCount;
    });

    setSearchResults(results);

    // Auto-switch to first tab with results ONLY when search query changes
    const firstTabWithResults = CATEGORIES.find(cat => results[cat.key] > 0);
    if (firstTabWithResults) {
      setActiveTab(firstTabWithResults.key);
    }
  }, [searchQuery, t]); // Removed activeTab from dependencies

  // Quick List functions
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      // Load all profiles initially (no search param for client-side filtering)
      const result = await fetchGatewaySipProfiles({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      setQuickListProfiles(result.data || []);
    } catch (error) {
      showError(t('gatewaySipProfiles:quickListError'));
      setQuickListProfiles([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [showError, t]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListProfiles.length === 0) {
      loadQuickList();
    }
  }, [showQuickList, quickListProfiles.length, loadQuickList]);

  // Client-side filtering
  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListProfiles;
    }

    return quickListProfiles.filter((profile) =>
      profile.name?.toLowerCase().includes(query) ||
      (profile.description || '').toLowerCase().includes(query)
    );
  }, [quickListProfiles, quickListSearch]);

  const handleQuickListSelect = useCallback((selectedProfile: GatewaySipProfile) => {
    setShowQuickList(false);
    setQuickListSearch('');
    navigate(`/settings/technology/gateway-profile/${selectedProfile.uuid ?? selectedProfile.id}`);
  }, [navigate]);

  // Duplicate current profile from the fixed header action
  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToGatewaySipProfileDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  const handleSubmit = useCallback(async (e?: React.FormEvent): Promise<GatewaySipProfile | undefined> => {
    // Prevent default form submission
    if (e) {
      e.preventDefault();
    }

    // Validation
    if (!formData.name.trim()) {
      showError(t('gatewaySipProfiles:nameRequired'));
      return undefined;
    }
    setIsSaving(true);
    try {
      // Map form data to settings array
      const settings = Object.entries(formData.settings)
        .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
        .map(([name, value]) => ({
          name,
          value: String(value),
          settingType: 'param',
        }));

      const payload: GatewaySipProfilePayload = {
        name: formData.name,
        description: formData.description || null,
        baseSipProfileId: formData.baseSipProfileId,
        isDefault: formData.isDefault,
        enabled: formData.enabled,
        settings,
      };

      if (tenantDomainId !== undefined) {
        payload.domainId = tenantDomainId;
      }

      let savedProfile;
      if (isEditMode) {
        const result = await updateGatewaySipProfile(id!, payload);
        savedProfile = result.data;
      } else {
        const result = await createGatewaySipProfile(payload);
        savedProfile = result.data;
      }

      showSuccess(
        isEditMode
          ? t('gatewaySipProfiles:updateSuccess')
          : t('gatewaySipProfiles:createSuccess')
      );

      // Update originalData to reflect saved state
      const syncedData = cloneFormData(formData);
      setOriginalData(syncedData);

      // Return saved data for useSaveWithAction
      if (savedProfile?.id) {
        return { id: Number(savedProfile.id), uuid: String(savedProfile.uuid || '') } as GatewaySipProfile;
      }
      return undefined;
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('gatewaySipProfiles:saveError');
      showError(msg);
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, id, isEditMode, showError, showInfo, showSuccess, t, tenantDomainId, cloneFormData]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as GatewaySipProfile;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/settings/technology/gateway-profile',
    newPath: '/settings/technology/gateway-profile/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Gateway SIP Profiles use ID for navigation
      return `/settings/technology/gateway-profile/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'gatewaySipProfiles:saveAndNewData',
  });

  // Handle cancel - restore original data (does NOT navigate)
  const handleCancel = () => {
    if (isEditMode) {
      // Restore original data
      setFormData(cloneFormData(originalData));
    } else {
      // Reset to defaults in create mode
      const defaultSettings: Record<string, string | boolean | number> = {};
      Object.values(FIELD_CONFIG).flat().forEach((field) => {
        defaultSettings[field.name] = field.default;
      });
      setFormData(cloneFormData({
        name: '',
        description: '',
        enabled: true,
        baseSipProfileId: null,
        isDefault: false,
        settings: defaultSettings,
      }));
    }
  };

  // Handle delete - show confirmation modal
  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  // Confirm delete - actually delete the item and navigate to list
  const confirmDelete = async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      await deleteGatewaySipProfile(id);

      showSuccess(t('gatewaySipProfiles:deleteSuccess') || 'Gateway SIP profile deleted successfully');
      setShowDeleteModal(false);

      // Navigate to list after successful delete
      navigate('/settings/technology/gateway-profile');
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('gatewaySipProfiles:deleteError');
      showError(msg || 'Failed to delete gateway SIP profile');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderField = (field: FieldDefinition) => {
    const value = formData.settings[field.name] ?? field.default;
    const isDisabled = field.dependsOn ? !formData.settings[field.dependsOn] : false;
    const label = t(`gatewaySipProfiles:${field.name}`);
    const tooltipText = t(`gatewaySipProfiles:tooltips.${field.name}`);
    const possibleValuesText = t(`gatewaySipProfiles:possibleValues.${field.name}`, {
      defaultValue: field.possibleValues ?? ''
    });
    const tooltipContent = [tooltipText, possibleValuesText ? `${t('gatewaySipProfiles:possibleValuesPrefix')}: ${possibleValuesText}` : '']
      .filter(Boolean)
      .join('\n\n') || label;

    const renderLabel = (labelClass?: string) => (
      <SimpleTooltip content={tooltipContent} position="top">
        <div className="flex flex-col">
          <label className={labelClass ?? 'text-sm font-medium text-foreground'}>
            {label}
          </label>
          {field.fsName && (
            <span className="text-xs text-muted-foreground mt-0.5">
              {field.fsName}
            </span>
          )}
        </div>
      </SimpleTooltip>
    );

    switch (field.type) {
      case 'codec':
        return (
          <React.Fragment key={field.name}>
            {renderLabel()}
            <CodecField
              value={String(value || '')}
              onChange={(newValue) =>
                setFormData((prev) => {
                  const newSettings = { ...prev.settings, [field.name]: newValue };
                  if (field.name === 'videoCodecPrefs') {
                    newSettings['allowVideo'] = newValue.length > 0;
                  }
                  return { ...prev, settings: newSettings };
                })
              }
              disabled={isDisabled}
              allowVideo={true} // Always allow video codecs for this field
              videoOnly={field.videoOnly === true}
              placeholder={t('gatewaySipProfiles:selectCodecs')}
            />
          </React.Fragment>
        );

      case 'toggle':
        return (
          <React.Fragment key={field.name}>
            {renderLabel('text-sm font-medium text-foreground cursor-pointer')}
            <div className="flex justify-start">
              <Toggle
                value={!!value}
                onChange={(newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    settings: { ...prev.settings, [field.name]: newValue },
                  }))
                }
                disabled={isDisabled}
              />
            </div>
          </React.Fragment>
        );

      case 'select':
        return (
          <React.Fragment key={field.name}>
            {renderLabel()}
            <select
              value={String(value)}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: { ...prev.settings, [field.name]: e.target.value },
                }))
              }
              disabled={isDisabled}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent disabled:opacity-50"
            >
              {(field.options || []).map((option) => (
                <option key={option} value={option}>
                  {t(`gatewaySipProfiles:${field.name}Options.${option}`) || option.toUpperCase()}
                </option>
              ))}
            </select>
          </React.Fragment>
        );

      case 'number':
        return (
          <React.Fragment key={field.name}>
            {renderLabel()}
            <input
              type="number"
              value={value !== undefined && value !== null ? Number(value) : ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    [field.name]: e.target.value
                      ? parseInt(e.target.value, 10)
                      : field.optional
                        ? null
                        : field.default,
                  },
                }))
              }
              min={field.min}
              max={field.max}
              disabled={isDisabled}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent disabled:opacity-50"
              placeholder={field.placeholder}
            />
          </React.Fragment>
        );

      case 'text':
      case 'password':
      default:
        return (
          <React.Fragment key={field.name}>
            {renderLabel()}
            <input
              type={field.type === 'password' ? 'password' : 'text'}
              value={String(value || '')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: { ...prev.settings, [field.name]: e.target.value },
                }))
              }
              disabled={isDisabled}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent disabled:opacity-50"
              placeholder={field.placeholder}
            />
          </React.Fragment>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Cable className="h-5 w-5 text-primary" />}
        title={isEditMode
          ? `${t('gatewaySipProfiles:editProfile')}: ${formData.name || t('gatewaySipProfiles:untitledProfile')}`
          : t('gatewaySipProfiles:createProfile')
        }
        backButtonText={t('common:list')}
        onBack={() => navigate('/settings/technology/gateway-profile')}
        searchInput={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('common:search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-10 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            {searchQuery && (
              <SimpleTooltip content={t('common:clear')} position="top">
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  
                </button>
              </SimpleTooltip>
            )}
          </div>
        }
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('gatewaySipProfiles:quickList') || 'Quick List',
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common:duplicate') || 'Duplicate',
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('gatewaySipProfiles:actions.add') || 'Add New',
            onClick: () => navigate('/settings/technology/gateway-profile/new'),
          },
        ]}
      />

      {/* Form */}
      <div className="mt-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Profile Information */}
          <div className="bg-card p-3 sm:p-6 rounded-lg border border-border">
            {/* Responsive Grid Layout: 4 cols on desktop, 2 on tablet, 1 on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              {/* Row 1 */}
              <SimpleTooltip
                content={t('gatewaySipProfiles:tooltips.profileName')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground">
                  {t('gatewaySipProfiles:profileName')}
                </label>
              </SimpleTooltip>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                placeholder="carrier-standard"
              />
              <SimpleTooltip
                content={t('gatewaySipProfiles:tooltips.description')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground">
                  {t('gatewaySipProfiles:description')}
                </label>
              </SimpleTooltip>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                placeholder="Optional description for this gateway profile"
              />

              {/* Row 2 */}
              <SimpleTooltip
                content={t('gatewaySipProfiles:tooltips.enabled')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground cursor-pointer">
                  {t('gatewaySipProfiles:enabled')}
                </label>
              </SimpleTooltip>
              <div className="flex justify-start">
                <Toggle
                  value={formData.enabled}
                  onChange={(value) => setFormData({ ...formData, enabled: value })}
                />
              </div>
              <SimpleTooltip
                content={t('gatewaySipProfiles:tooltips.sofiaProfileId')}
                position="top"
              >
                <label className="text-sm font-medium text-foreground">
                  {t('gatewaySipProfiles:sofiaProfileId')}
                </label>
              </SimpleTooltip>
              <select
                value={formData.baseSipProfileId?.toString() || ''}
                onChange={(e) => setFormData({ ...formData, baseSipProfileId: e.target.value ? parseInt(e.target.value) : null })}
                className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
              >
                <option value="">{t('common:selectOption')}</option>
                {sipProfiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Settings Tabs */}
          <GatewaySipProfileSettingsTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            categories={CATEGORIES}
            fieldConfig={FIELD_CONFIG}
            searchQuery={searchQuery}
            searchResults={searchResults}
            renderField={renderField}
            t={t}
          />
        </form>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? handleDelete : undefined}
        isEditMode={isEditMode}
        saveText={t('common:save')}
        cancelText={t('common:cancel')}
        deleteText={t('common:delete') || 'Delete'}
        saveDisabled={isSaving}
        isLoading={isSaving}
        useSaveDropdown={true}
        saveDropdownStorageKey="gatewaySipProfiles:saveActionPreference"
        hasChanges={hasChanges}
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('gatewaySipProfiles:quickListTitle')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(profile) => handleQuickListSelect(profile)}
        currentItemId={id}
        renderItem={(profile, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              handleQuickListSelect(profile);
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
                ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
                : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">{profile.name}</div>
            {profile.description && (
              <div className="text-sm text-muted-foreground mt-1">
                {profile.description}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {profile.enabled ? t('common:enabled') : t('common:disabled')}
              {'  '}
              {t('gatewaySipProfiles:settingsCount', { count: profile._count?.settings ?? 0 })}
            </div>
          </button>
        )}
        searchPlaceholder={t('gatewaySipProfiles:quickListSearchPlaceholder')}
        emptyMessage={t('gatewaySipProfiles:quickListEmpty.default')}
        noResultsMessage={t('gatewaySipProfiles:quickListEmpty.search')}
        headerIcon={<Cable className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('gatewaySipProfiles:deleteConfirmTitle') || 'Delete Gateway SIP Profile'}
        message={t('gatewaySipProfiles:deleteConfirmMessage') || `Are you sure you want to delete this gateway SIP profile? This action cannot be undone.`}
        confirmText={t('gatewaySipProfiles:deleteConfirm') || t('common:delete') || 'Delete'}
        cancelText={t('common:cancel') || 'Cancel'}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
